import { ChangeDetectionStrategy, Component, inject, OnInit, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Subject } from 'rxjs';

import { BaseComponent } from '../../../core/abstractions/base.component';
import { AgendaItemModel } from '../../../models/agenda-item.model';
import { VoteOptionEnum } from '../../../models/enums/vote-option.enum';
import { GeneralMeetingsResource } from '../../../services/resources/general-meetings.resource';
import { SignalRService } from '../../../services/signalr.service';

@Component({
    selector: 'sl-voting-dialog',
    templateUrl: './voting-dialog.component.html',
    styleUrls: ['./voting-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class VotingDialogComponent extends BaseComponent implements OnInit {
    readonly VoteOptionEnum = VoteOptionEnum;
    readonly currentVoting = signal<AgendaItemModel[]>([]);
    readonly isLoading = signal(true);

    private readonly onUserVoted = inject<Subject<void>>(MAT_DIALOG_DATA);
    private readonly dialogRef = inject(MatDialogRef);
    private readonly generalMeetingsResource = inject(GeneralMeetingsResource);
    private readonly signalRService = inject(SignalRService);

    ngOnInit(): void {
        const msgSub = this.signalRService.onMessageReceived.subscribe(() => this.loadVoting());

        this.loadVoting(true);
        this.addSubscriptions(msgSub);
    }

    vote(item: AgendaItemModel, option: VoteOptionEnum): void {
        this.generalMeetingsResource.voteOnAgendaItem(item.gmAgendaItemNumber, option).subscribe(() => {
            this.loadVoting();
            this.onUserVoted.next();
        });
    }

    getChildren(item: AgendaItemModel): AgendaItemModel[] {
        return this.currentVoting().filter(i => i.parentGmAgendaItemNumber === item.gmAgendaItemNumber);
    }

    hasChildren(item: AgendaItemModel): boolean {
        return this.getChildren(item).length > 0;
    }

    private loadVoting(isInitialLoad = false): void {
        this.generalMeetingsResource.getCurrentAgendaItemsForShareholder().subscribe(r => {
            this.currentVoting.set(
                r.value
                    .filter(
                        ci =>
                            ci.isVotingOpen
                            || r.value.findIndex(inn => inn.parentGmAgendaItemNumber === ci.gmAgendaItemNumber && inn.isVotingOpen) >= 0
                    )
                    .map(i => {
                        if (i.isVotingOpen) {
                            return i;
                        }

                        const latestChild = r.value
                            .filter(child => child.parentGmAgendaItemNumber === i.gmAgendaItemNumber)
                            .sort(
                                (a, b) =>
                                    // Sort by votingStartedDateTime DESC.
                                    (b.votingStartedDateTime?.getTime() ?? 0) - (a.votingStartedDateTime?.getTime() ?? 0)
                            )[0];
                        // setting parents voting started time to that of first child for display purposes
                        i.votingStartedDateTime = latestChild.votingStartedDateTime;

                        return i;
                    })
                    .sort(
                        (a, b) =>
                            // Sort by votingStartedDateTime DESC.
                            (b.votingStartedDateTime?.getTime() ?? 0) - (a.votingStartedDateTime?.getTime() ?? 0)
                    )
            );

            if (this.currentVoting().length === 0 && !isInitialLoad) {
                this.dialogRef.close();
            }

            this.isLoading.set(false);
        });
    }
}
